import isNil from 'lodash/isNil';

import type { EmailNotificationType } from '../../../../../graphql';
import type { XcEmailSubscriptions } from '../../../../../types/domain/self/profile';
import type { XcNotificationSettingPartsFragment } from './graphql/__generated__';

export const NOTIFICATION_TYPE_METADATA = {
  OFFERING_STATUS_CHANGE: {
    label: 'Offering Status Change',
    description:
      'Email sent when the status of an offering changes to Live, Priced, Postponed, Terminated, or Withdrawn.',
    category: 'Offering',
  },
  ORDER_BOOK_OPENED: {
    label: 'Order Book Opened',
    description: 'Email sent when the order book opens and begins accepting indications.',
    category: 'Order Book',
  },
  ORDER_BOOK_CLOSED: {
    label: 'Order Book Closed',
    description: 'Email sent when the order book is closed.',
    category: 'Order Book',
  },
  NEW_INDICATION_SUBMITTED: {
    label: 'New Indication Submitted',
    description: 'Email sent when a new indication is submitted.',
    category: 'Indication',
  },
  INDICATION_MODIFIED: {
    label: 'Indication Modified',
    description:
      'Email sent when an existing indication is modified (e.g., quantity, price, unit, or type) or marked as a pass.',
    category: 'Indication',
  },
  INDICATION_CANCELLED: {
    label: 'Indication Cancelled',
    description: 'Email sent when an existing indication is cancelled.',
    category: 'Indication',
  },
  INDICATION_DUPLICATED: {
    label: 'Indication Duplicated',
    description: 'Email sent when an indication is marked or unmarked as a duplicate.',
    category: 'Indication',
  },
  PRIVATE_PLACEMENT_RESPONSE: {
    label: 'Private Placement Response',
    description:
      'Email sent when an investor responds Interested or Pass on a Private Placement offering.',
    category: 'Indication',
  },
  RETAIL_DEMAND_SUBMITTED: {
    label: 'Retail Demand Submitted',
    description: 'Email sent when retail demand is submitted for an offering.',
    category: 'Retail',
  },
  FINAL_ALLOCATION_RELEASED: {
    label: 'Final Allocation Released',
    description: 'Email sent when final allocations are released by the settlement agent.',
    category: 'Allocation',
  },
  FINAL_ALLOCATION_UPDATED: {
    label: 'Final Allocations Updated',
    description: 'Email sent when released allocations are modified by the settlement agent.',
    category: 'Allocation',
  },
  FINAL_ALLOCATION_ACKNOWLEDGED_REJECTED: {
    label: 'Final Allocation Acknowledged/Rejected',
    description: 'Email sent when final allocations are acknowledged or rejected.',
    category: 'Allocation',
  },
} as const;

export type NotificationCategoryItem = {
  notificationType: string;
  label: string;
  description: string;
};

export type NotificationsByCategory = {
  [categoryName: string]: NotificationCategoryItem[];
};

export function toXcEmailInitialValues(
  xcNotifications: readonly XcNotificationSettingPartsFragment[]
): XcEmailSubscriptions {
  const initialValues = xcNotifications.reduce<Partial<XcEmailSubscriptions>>(
    (acc, notification) => {
      if (!isNil(notification.notificationType)) {
        return {
          ...acc,
          [notification.notificationType]: notification.isEnabled,
        };
      }
      return acc;
    },
    {}
  );

  return initialValues as XcEmailSubscriptions;
}

export function fromXcEmailFormValues(
  formValues: XcEmailSubscriptions,
  originalNotifications: readonly XcNotificationSettingPartsFragment[]
): { emailNotificationType: EmailNotificationType; isActive: boolean }[] {
  return originalNotifications
    .filter(n => !isNil(n.notificationType))
    .map(n => {
      const value = formValues[n.notificationType!] ?? false;

      return {
        emailNotificationType: n.notificationType!,
        isActive: value,
      };
    });
}

export function groupNotificationsByCategory(
  xcNotifications: readonly XcNotificationSettingPartsFragment[]
): NotificationsByCategory {
  const result: NotificationsByCategory = {};

  xcNotifications.forEach(notification => {
    if (!notification.notificationType) return;

    const metadata =
      NOTIFICATION_TYPE_METADATA[
        notification.notificationType as keyof typeof NOTIFICATION_TYPE_METADATA
      ];

    if (metadata) {
      const category = metadata.category;
      if (!result[category]) {
        result[category] = [];
      }
      result[category].push({
        notificationType: notification.notificationType,
        label: metadata.label,
        description: metadata.description,
      });
    }
  });

  return result;
}
